/**
 * Google Consent Mode v2 hooks.
 * https://developers.google.com/tag-platform/security/guides/consent
 *
 * The SDK pushes a `default: denied` immediately at load time so any
 * tag fired before the user has interacted honors the denial. When the
 * user makes a choice we push an `update` reflecting their categories.
 */

import type { ConsentChoices } from "./types.js";

// biome-ignore lint/suspicious/noExplicitAny: gtag is intentionally loose-typed by Google.
type GtagFn = (...args: any[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

function ensureGtag(): GtagFn {
  if (typeof window === "undefined") return () => {};
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    // Note: Google's canonical snippet uses `arguments` to forward the call
    // to dataLayer; rest parameters serialize identically once iterated by
    // GTM, which doesn't care whether the entry is an Arguments or an Array.
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
  }
  return window.gtag;
}

export const GCM_DEFAULT_DENIED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "granted",
  personalization_storage: "denied",
  security_storage: "granted",
} as const;

export function declareDefaults(): void {
  const gtag = ensureGtag();
  gtag("consent", "default", {
    ...GCM_DEFAULT_DENIED,
    wait_for_update: 500,
  });
}

export function updateFromChoices(choices: ConsentChoices): void {
  const gtag = ensureGtag();
  const grant = (allowed: boolean) => (allowed ? "granted" : "denied");
  gtag("consent", "update", {
    ad_storage: grant(choices.marketing),
    ad_user_data: grant(choices.marketing),
    ad_personalization: grant(choices.marketing),
    analytics_storage: grant(choices.analytics),
    functionality_storage: "granted",
    personalization_storage: grant(choices.personalization),
    security_storage: "granted",
  });
}
